import { styled } from '@mui/material/styles';
import { Button as MuiButton } from '@mui/material';
import { ButtonSize, ButtonVariant } from './Button.types';
import { designTokens } from '../../tokens';

/**
 * Size-specific styles for buttons.
 *
 * IMPORTANT: this is a function, not a plain object, and `designTokens` is
 * only read once it's called — never at module-evaluation time. That's
 * deliberate: if `designTokens` is ever imported as part of a circular
 * import chain, reading it eagerly at the top of this module (before the
 * whole module graph has finished initializing) throws
 * "Cannot access 'designTokens' before initialization". Wrapping it in a
 * function defers the read until the styled() callback actually runs
 * (on first render), by which point the module graph has settled and
 * designTokens is safely assigned.
 */
const getSizeStyles = (size: ButtonSize) => {
  const map: Record<ButtonSize, any> = {
    sm: {
      padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
      fontSize: designTokens.typography.fontSize.xs,
      height: '32px',
      minWidth: '64px',
    },
    md: {
      padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
      fontSize: designTokens.typography.fontSize.sm,
      height: '40px',
      minWidth: '80px',
    },
    lg: {
      padding: `${designTokens.spacing[3]} ${designTokens.spacing[6]}`,
      fontSize: designTokens.typography.fontSize.base,
      height: '48px',
      minWidth: '96px',
    },
    xl: {
      padding: `${designTokens.spacing[4]} ${designTokens.spacing[8]}`,
      fontSize: designTokens.typography.fontSize.lg,
      height: '56px',
      minWidth: '112px',
    },
  };

  return map[size];
};

/**
 * Variant-specific styles for buttons.
 * Already a function in the original file — kept as-is, since calling a
 * function defers the designTokens read to call time, which is correct.
 */
const getVariantStyles = (variant: ButtonVariant) => {
  const tokens = designTokens.colors;

  switch (variant) {
    case 'primary':
      return {
        background: `linear-gradient(135deg, ${tokens.primary[500]} 0%, ${tokens.primary[600]} 100%)`,
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        border: 'none',
        '&:hover': {
          background: `linear-gradient(135deg, ${tokens.primary[600]} 0%, ${tokens.primary[700]} 100%)`,
          boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:disabled': {
          background: designTokens.colors.neutral[300],
          color: designTokens.colors.neutral[500],
          boxShadow: 'none',
        },
      };

    case 'secondary':
      return {
        background: `linear-gradient(135deg, ${tokens.secondary[600]} 0%, ${tokens.secondary[500]} 100%)`,
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)',
        border: 'none',
        '&:hover': {
          background: `linear-gradient(135deg, ${tokens.secondary[700]} 0%, ${tokens.secondary[600]} 100%)`,
          boxShadow: '0 6px 16px rgba(13, 148, 136, 0.4)',
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:disabled': {
          background: designTokens.colors.neutral[300],
          color: designTokens.colors.neutral[500],
          boxShadow: 'none',
        },
      };

    case 'outline':
      return {
        background: 'transparent',
        color: tokens.primary[600],
        border: `2px solid ${tokens.primary[500]}`,
        boxShadow: 'none',
        '&:hover': {
          background: tokens.primary[50],
          borderColor: tokens.primary[600],
          color: tokens.primary[700],
        },
        '&:disabled': {
          background: 'transparent',
          borderColor: designTokens.colors.neutral[300],
          color: designTokens.colors.neutral[400],
        },
      };

    case 'ghost':
      return {
        background: 'transparent',
        color: tokens.primary[600],
        border: 'none',
        boxShadow: 'none',
        '&:hover': {
          background: tokens.primary[50],
          color: tokens.primary[700],
        },
        '&:disabled': {
          background: 'transparent',
          color: designTokens.colors.neutral[400],
        },
      };

    case 'danger':
      return {
        background: `linear-gradient(135deg, ${tokens.error[500]} 0%, ${tokens.error[700]} 100%)`,
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
        border: 'none',
        '&:hover': {
          background: `linear-gradient(135deg, ${tokens.error[700]} 0%, ${tokens.error[900]} 100%)`,
          boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)',
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:disabled': {
          background: designTokens.colors.neutral[300],
          color: designTokens.colors.neutral[500],
          boxShadow: 'none',
        },
      };

    case 'text':
      return {
        background: 'transparent',
        color: tokens.primary[600],
        border: 'none',
        boxShadow: 'none',
        padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
        '&:hover': {
          background: 'rgba(16, 185, 129, 0.04)',
          color: tokens.primary[700],
        },
        '&:disabled': {
          background: 'transparent',
          color: designTokens.colors.neutral[400],
        },
      };

    default:
      return {};
  }
};

/**
 * Styled Button component with design system tokens.
 *
 * The styled() callback (({ buttonVariant, buttonSize }) => ({...})) only
 * runs when MUI actually needs to compute styles for a rendered instance —
 * never at module-import time. Both getSizeStyles() and getVariantStyles()
 * are now only ever called from inside this callback, so designTokens is
 * always read lazily, at render time, never during module evaluation.
 */
export const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    !['buttonVariant', 'buttonSize'].includes(prop as string),
})<{ buttonVariant: ButtonVariant; buttonSize: ButtonSize }>(
  ({ buttonVariant, buttonSize }) => ({
    ...getSizeStyles(buttonSize),
    ...getVariantStyles(buttonVariant),
    borderRadius: designTokens.borderRadius.lg,
    textTransform: 'none',
    fontWeight: designTokens.typography.fontWeight.semibold,
    fontFamily: designTokens.typography.fontFamily.primary,
    transition: `all ${designTokens.transitions.duration.fast} ${designTokens.transitions.timing.easeInOut}`,
    cursor: 'pointer',

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  })
);
